import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import AvatarLayeredPreview from '../../../components/AvatarLayeredPreview';

const EditUserModal = ({ isOpen, onClose, onSubmit, user }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'student'
  });

  const [errors, setErrors] = useState({});

  const roleOptions = [
    { value: 'student', label: 'Student' },
    { value: 'admin', label: 'Admin' }
  ];

  useEffect(() => {
    if (user) {
      setFormData({
        name: user?.name,
        email: user?.email,
        role: user?.role
      });
    }
  }, [user]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit({ ...user, ...formData });
      handleClose();
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  if (!isOpen || !user) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-foreground/50 z-40 animate-fade-in"
        onClick={handleClose}
        aria-hidden="true"
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto animate-scale-in">
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h2 className="text-xl font-semibold text-card-foreground flex items-center gap-2">
              <Icon name="Edit" size={24} />
              Edit User
            </h2>
            <button
              onClick={handleClose}
              className="p-2 rounded-md hover:bg-muted transition-colors duration-150"
              aria-label="Close modal"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Avatar preview section */}
          {user?.id && (
            <div className="px-6 pt-5 pb-2 flex flex-col items-center gap-1 border-b border-border/50">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">User Avatar</p>
              <AvatarLayeredPreview
                userId={user?.id}
                size={110}
                showEditButton={true}
                onEditClick={() => {
                  handleClose();
                  navigate('/student-avatar-studio');
                }}
              />
            </div>
          )}

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <Input
              label="Full Name"
              type="text"
              placeholder="Enter full name"
              value={formData?.name}
              onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
              error={errors?.name}
              required
            />

            <Input
              label="Email Address"
              type="email"
              placeholder="user@example.com"
              value={formData?.email}
              onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
              error={errors?.email}
              required
            />

            <Select
              label="User Role"
              options={roleOptions}
              value={formData?.role}
              onChange={(value) => setFormData({ ...formData, role: value })}
              required
              description={user?.role === 'superadmin' ? 'Superadmin role cannot be changed' : ''}
              disabled={user?.role === 'superadmin'}
            />

            <div className="flex items-center gap-3 pt-4">
              <Button
                type="submit"
                variant="default"
                fullWidth
                iconName="Save"
                iconPosition="left"
              >
                Save Changes
              </Button>
              <Button
                type="button"
                variant="outline"
                fullWidth
                onClick={handleClose}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditUserModal;